import * as React from 'react'
import cn from 'classnames'
import { Icon } from 'src/design-system/components/atoms/Icon'
import { ChevronUp, ChevronDown } from '@easy-eva-icons/react'
import * as Table from 'src/design-system/components/molecules/Table'
import styles from './CollapsibleTable.module.scss'

export type CollapsibleTableProps = {
  className?: string
  // these can likely be derived from the data.
  // might want to update once the API is in place.
  headings: Record<string, string>
  groupedRows: GroupedRow[]
}

// REVISIT: when the shape of the data from the API is decided,
// we'll likely want to change how tags are shown for skills or headings.
// right now, the table is ignorant to its ability to render tags.
// it passes that responsibility off to its call site.
// this may or may not be ergonomic when we glue everything together.
type GroupedRow = {
  name: string
  display: React.ReactNode
  rows: Row[]
}

type Row = Record<string | number, React.ReactNode>

/**
 * Display data grouped under specific names.
 * Rows under each group are collapsible.
 * For tracking collapsed state, this component assumes no two groups have the same name.
 */
export const CollapsibleTable: React.VFC<CollapsibleTableProps> = ({
  className,
  headings,
  groupedRows,
}) => {
  const [collapsed, setCollapsed] = React.useState(new Set())
  const numColumns = React.useMemo(
    () => Object.values(headings).length,
    [headings]
  )

  const toggle = (name: string) => {
    setCollapsed((set) => {
      const newSet = new Set(set)
      if (newSet.has(name)) {
        newSet.delete(name)
      } else {
        newSet.add(name)
      }
      return newSet
    })
  }

  return (
    <Table.Root
      className={cn(styles.tableContainer, className)}
      tableClassNames={styles.table}
    >
      <Table.Head>
        <Table.Row>
          {Object.values(headings).map((label) => (
            <Table.Header className={styles.th} key={label}>
              {label}
            </Table.Header>
          ))}
        </Table.Row>
      </Table.Head>
      {groupedRows.map(({ display, rows, name }) => (
        <>
          <Table.Body>
            <Table.Row className={styles.collapsibleRow} key={name}>
              <Table.Cell
                className={styles.collapsibleCell}
                colSpan={numColumns}
              >
                <button onClick={() => toggle(name)}>
                  <p>{display}</p>
                  <Icon size="sm">
                    {collapsed.has(name) ? <ChevronUp /> : <ChevronDown />}
                  </Icon>
                </button>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
          {!collapsed.has(name) && (
            <Table.Body>
              {rows.map((row, rowIndex) => (
                <Table.Row className={styles.row} key={rowIndex}>
                  {Object.values(row).map((value, cellIndex) => (
                    <Table.Cell className={styles.cell} key={cellIndex}>
                      {value}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          )}
        </>
      ))}
    </Table.Root>
  )
}
